import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Box, Typography, Alert, CircularProgress, Button } from '@mui/material';
import { AppColors } from '../theme/colors';
import { useAuth } from '../hooks/useAuth';

/**
 * 決済完了後の着地点（/registration-complete）
 *
 * Webhook が会社情報を作り終えるのを待ってから、管理者アカウントで自動ログインし
 * 管理画面へ送る。旧ログイン画面（/login）が担っていた処理をそのまま移したもの。
 */
export const RegistrationCompletePage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const [isProcessing, setIsProcessing] = useState(true);
  const [message, setMessage] = useState<string | null>('決済が完了しました。会社情報を確認中です...');
  const [failed, setFailed] = useState(false);
  const [shouldNavigateToAdmin, setShouldNavigateToAdmin] = useState(false);

  // 管理者セッションが認識されたら管理画面に遷移
  useEffect(() => {
    if (!shouldNavigateToAdmin) return;

    if (user && (user.userType === 'admin' || user.userType === 'superadmin')) {
      setShouldNavigateToAdmin(false);
      navigate('/admin', { replace: true });
      return;
    }

    // userがまだ更新されていない場合、localStorageを直接チェックして遷移
    const localStorage = typeof window !== 'undefined' ? window.localStorage : null;
    if (localStorage && localStorage.getItem('adminUserId')) {
      // useAuthが更新されるのを少し待つ（最大3秒）
      const checkInterval = setInterval(() => {
        if (user && (user.userType === 'admin' || user.userType === 'superadmin')) {
          clearInterval(checkInterval);
          setShouldNavigateToAdmin(false);
          navigate('/admin', { replace: true });
        }
      }, 200);

      // 3秒後にタイムアウトして直接遷移
      const timeout = setTimeout(() => {
        clearInterval(checkInterval);
        console.warn('[決済完了] タイムアウト: 管理者セッションの認識を待たずに遷移を試みます');
        setShouldNavigateToAdmin(false);
        navigate('/admin', { replace: true });
      }, 3000);

      return () => {
        clearInterval(checkInterval);
        clearTimeout(timeout);
      };
    }
  }, [user, shouldNavigateToAdmin, navigate]);

  useEffect(() => {
    const token = searchParams.get('token');

    if (!token) {
      setIsProcessing(false);
      setFailed(true);
      setMessage('登録情報が見つかりません。');
      return;
    }

    const run = async () => {
      try {
        const { doc, getDoc } = await import('firebase/firestore');
        const { db } = await import('../firebase/config');

        // Webhookの完了を待つ（最大10秒、0.5秒間隔でリトライ）
        let companyId: string | null = null;
        for (let i = 0; i < 20; i++) {
          const tempDoc = await getDoc(doc(db, 'temp_registrations', token));

          if (!tempDoc.exists()) {
            setIsProcessing(false);
            setFailed(true);
            setMessage('決済情報が見つかりません。');
            return;
          }

          companyId = tempDoc.data()?.companyId;
          if (companyId) break;

          await new Promise((resolve) => setTimeout(resolve, 500));
        }

        if (!companyId) {
          setIsProcessing(false);
          setFailed(true);
          setMessage('会社情報の取得中です。しばらくお待ちいただき、画面を再読み込みしてください。');
          return;
        }

        const companyDoc = await getDoc(doc(db, 'companies', companyId));
        if (!companyDoc.exists()) {
          setIsProcessing(false);
          setFailed(true);
          setMessage('会社情報が見つかりません。');
          return;
        }
        const companyData = companyDoc.data();

        const tempDoc = await getDoc(doc(db, 'temp_registrations', token));
        if (!tempDoc.exists()) {
          setIsProcessing(false);
          setFailed(true);
          setMessage('登録情報が見つかりません。');
          return;
        }
        const tempData = tempDoc.data();

        // 登録情報をsessionStorageに保存（会社登録後のフラグも追加）
        // 管理者パスワードは本人が入力した値のため保存しない
        sessionStorage.setItem('registrationInfo', JSON.stringify({
          corporateCode: companyData?.corporateCode || '',
          contactEmail: tempData?.contactEmail || '',
          isNewRegistration: true, // 会社登録後のフラグ
        }));

        setMessage(`会社登録が完了しました！\n会社名: ${companyData?.companyName}\n\n自動ログイン中...`);

        // 管理者アカウントで自動ログインし、管理画面へ送る
        try {
          const { AuthService } = await import('../services/authService');

          await AuthService.signInWithEmailPassword(
            companyData?.corporateCode,
            tempData?.contactEmail,
            tempData?.adminPassword
          );

          setMessage('ログインに成功しました！管理画面に移動します...');

          // localStorageにadminUserIdが保存されるまで待つ（最大2秒）
          for (let i = 0; i < 20; i++) {
            const localStorage = typeof window !== 'undefined' ? window.localStorage : null;
            if (localStorage && localStorage.getItem('adminUserId')) break;
            await new Promise((resolve) => setTimeout(resolve, 100));
          }

          // useAuthフックが管理者セッションを認識する時間を確保する
          await new Promise((resolve) => setTimeout(resolve, 500));
          setShouldNavigateToAdmin(true);
        } catch (loginError) {
          console.error('[決済完了] 自動ログインエラー:', loginError);
          setFailed(true);
          setMessage(
            `会社登録が完了しました！\n会社名: ${companyData?.companyName}\n会社ID: ${companyData?.corporateCode}\n\n` +
            '恐れ入りますが、管理者ログイン画面からログインしてください。'
          );
        }
      } catch (error) {
        console.error('[決済完了] エラー:', error);
        setFailed(true);
        setMessage('決済処理中にエラーが発生しました。');
      } finally {
        setIsProcessing(false);
      }
    };

    run();
  }, [searchParams]);

  return (
    <Box
      sx={{
        minHeight: '100vh',
        background: '#ffffff',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        p: 3,
      }}
    >
      <Box sx={{ maxWidth: 400, width: '100%', textAlign: 'center' }}>
        <Box
          component="img"
          src="/icon.svg"
          alt="LiaVoice"
          sx={{ width: 96, height: 96, margin: '0 auto', display: 'block' }}
        />
        <Typography
          variant="h4"
          sx={{
            fontSize: '1.5rem',
            fontWeight: 800,
            color: AppColors.primary,
            letterSpacing: '1.0px',
            fontFamily: '"Nunito", "Noto Sans JP", sans-serif',
            mt: 1,
            mb: 4,
          }}
        >
          LiaVoice
        </Typography>

        {isProcessing && <CircularProgress sx={{ mb: 2 }} />}

        {message && (
          isProcessing ? (
            <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'pre-line' }}>
              {message}
            </Typography>
          ) : (
            <Alert severity={failed ? 'error' : 'success'} sx={{ mb: 3, whiteSpace: 'pre-line', textAlign: 'left' }}>
              {message}
            </Alert>
          )
        )}

        {failed && (
          <Button variant="outlined" onClick={() => navigate('/admin-login', { replace: true })}>
            管理者ログイン画面へ
          </Button>
        )}
      </Box>
    </Box>
  );
};

export default RegistrationCompletePage;
